import { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";
import { useLanguage } from "./LanguageProvider";
import { ChevronLeft, ChevronRight } from "lucide-react";

const bookPages = [
  { img: "/media/photos/Bridgette.Gubelin.1.jpeg", caption: "Cover — The Gübelin Gemological Profile" },
  { img: "/media/photos/Bridgette.Gubelin.2.jpeg", caption: "Specimen Analysis — Origin & Classification" },
  { img: "/media/photos/Bridgette.Gubelin.3.jpeg", caption: "Photographic Documentation" },
  { img: "/media/photos/Bridgette.Gubelin.4.jpeg", caption: "Certification & Authentication" },
];

export default function CertificationSection() {
  const sectionRef = useSectionTracking("certification");
  const { t } = useLanguage();
  const c = t.cert;
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-80px" });
  const [currentPage, setCurrentPage] = useState(0);

  const nextPage = () => setCurrentPage(p => (p + 1) % bookPages.length);
  const prevPage = () => setCurrentPage(p => (p - 1 + bookPages.length) % bookPages.length);

  return (
    <section id="certification" ref={sectionRef} className="relative py-12 lg:py-20 overflow-hidden" data-testid="certification-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={animRef}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
          <p className="overline mb-4">{c.overline}</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-6">
            {c.title} <span className="italic gold-gradient-text">{c.title_accent}</span>
          </h2>
          <p className="text-base text-[#B8B3AA] max-w-xl font-light leading-relaxed mb-6">{c.desc}</p>
          <p className="text-sm text-[#B8B3AA]/70 italic max-w-lg leading-relaxed mb-8 border-l border-[#C5A059]/20 pl-4">
            Since 1854, Gübelin Gem Lab has set the global standard for gemological certification. Their decision to produce a premium Gemological Profile for a raw emerald — a first in 170 years of operation — speaks to the singular nature of this specimen.
            <span className="block text-xs text-[#B8B3AA]/75 mt-2 not-italic">Source: Gübelin Gem Lab, Zurich</span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Book Viewer */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative bg-[#1A1918] border border-[#C5A059]/10 p-3 sm:p-4" style={{ boxShadow: "20px 20px 60px rgba(0,0,0,0.5)" }}>
              <div className="relative overflow-hidden aspect-[3/4]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={currentPage}
                    src={bookPages[currentPage].img}
                    alt={bookPages[currentPage].caption}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.5 }}
                    className="w-full h-full object-cover"
                    data-testid={`gubelin-photo-${currentPage + 1}`}
                  />
                </AnimatePresence>
              </div>
              <div className="flex items-center justify-between mt-3 px-1">
                <button onClick={prevPage} className="text-[#B8B3AA]/75 hover:text-[#C5A059] transition-colors" data-testid="book-prev">
                  <ChevronLeft size={20} strokeWidth={1.5} />
                </button>
                <p className="text-xs text-[#B8B3AA]/70 italic text-center flex-1">{bookPages[currentPage].caption}</p>
                <button onClick={nextPage} className="text-[#B8B3AA]/75 hover:text-[#C5A059] transition-colors" data-testid="book-next">
                  <ChevronRight size={20} strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex justify-center gap-1.5 mt-3">
                {bookPages.map((_, i) => (
                  <button key={i} onClick={() => setCurrentPage(i)} className={`w-1.5 h-1.5 transition-all ${i === currentPage ? "bg-[#C5A059] w-4" : "bg-[#B8B3AA]/20"}`} />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            <div>
              <h3 className="font-serif text-2xl lg:text-3xl text-[#F4F0EA] mb-4">{c.report_title}</h3>
              <p className="text-base text-[#B8B3AA] leading-relaxed font-light">{c.report_desc}</p>
            </div>

            <div className="grid grid-cols-2 gap-0 border border-[#C5A059]/10">
              {[
                [c.species, c.natural_beryl],
                [c.origin, c.colombia],
                [c.shape, c.hex_prism],
                [c.enh, c.none_detected],
              ].map(([label, val], i) => (
                <div key={i} className="p-5 border-b border-r border-[#C5A059]/10 last:border-r-0 [&:nth-child(2)]:border-r-0 [&:nth-child(3)]:border-b-0 [&:nth-child(4)]:border-b-0">
                  <p className="text-xs text-[#C5A059]/50 uppercase tracking-wider mb-1">{label}</p>
                  <p className="text-base text-[#F4F0EA]/80">{val}</p>
                </div>
              ))}
            </div>

            <div className="border-l-2 border-[#C5A059]/30 pl-6">
              <h3 className="font-serif text-xl lg:text-2xl text-[#F4F0EA] mb-3">{c.book_title}</h3>
              <p className="text-base text-[#B8B3AA] leading-relaxed font-light mb-4">{c.book_desc}</p>
              <p className="text-base text-[#B8B3AA] leading-relaxed font-light">
                <span className="text-[#C5A059] font-medium">{c.book_highlight}</span> — {c.book_detail}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
